import React, { Component } from "react";
import axios from "axios";
import {
  PrismButton,
  PrismInputText,
  PrismInputTextarea,
  PrismSelect,
} from "@prism-ui/react";
import Loader from "../Loader";
import "./Event.css";

export default class EventForm extends Component {
  state = {
    loading: true,
    error: null,
    roles: [],
    event: {
      name: "",
      description: "",
      date: "",
      type: "event",
      role: "",
      chapter: this.props.chapter,
      EventLinks: [],
    },
    newLinkName: "",
    newLinkUrl: "",
  };

  componentDidMount() {
    this.loadRoles().then(() => {
      this.loadEvent(this.props.id);
    }).catch((error) => {
      // BUG FIX: Handle promise rejection
      console.error('Failed to load roles:', error);
      this.setState({ loading: false });
    });

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleLinkAdd = this.handleLinkAdd.bind(this);
    this.handleLinkRemove = this.handleLinkRemove.bind(this);
  }

  componentDidUpdate() {
    if (!this.props.id && this.state.event.id) {
      this.resetEvent();
    } else if (this.props.id != this.state.event.id) {
      this.loadEvent(this.props.id);
    }
  }

  loadRoles() {
    return axios
      .get(`/api/roles/`)
      .then((response) => {
        var roles = response.data
          .sort((a, b) => {
            return b.permission_level - a.permission_level;
          })
          .filter((role) => role.permission_level >= 65);

        this.setState({
          roles: roles,
        });
      })
      .catch((error) => {
        // Logged here rather than in the componentDidMount catch: this handler
        // resolves the promise, so the outer catch never runs.
        console.error('Failed to load roles:', error);
        this.setState({
          loading: false,
          error: "Unable to load roles!",
        });
      });
  }

  loadEvent(id) {
    if (!id) {
      this.resetEvent();
      return;
    }

    return axios
      .get(`/api/events/${id}`)
      .then((response) => {
        this.setState({
          loading: false,
          event: response.data,
        });
      })
      .catch((error) => {
        this.setState({
          loading: false,
          error: "Unable to load form!",
        });
      });
  }

  resetEvent() {
    // BUG FIX: Check if roles array has items before accessing
    const defaultRoleId = this.state.roles.length > 0 ? this.state.roles[0].id : null;
    this.setState({
      loading: false,
      event: {
        name: "",
        description: "",
        date: "",
        type: "event",
        role: defaultRoleId,
        chapter: this.props.chapter,
        EventLinks: [],
      },
    });
  }

  // Handlers
  // Prism inputs report their value on `event.detail` rather than on a native
  // target, so the field name is captured here instead of read off the event.
  handlePrismChange = (name) => (e) => {
    const event = this.state.event;
    event[name] = e.detail?.value ?? "";

    this.setState({ event });
  };

  handleLinkAdd(e) {
    e.preventDefault();

    let event = this.state.event;
    event.EventLinks.push({
      name: this.state.newLinkName,
      url: this.state.newLinkUrl,
    });

    this.setState({
      event: event,
      newLinkName: "",
      newLinkUrl: "",
    });
  }

  handleLinkRemove(index) {
    let event = this.state.event;
    event.EventLinks.splice(index, 1);

    this.setState({
      event: event,
    });
  }

  handleSubmit(e) {
    e.preventDefault();

    // Validate Form
    let form = e.currentTarget;
    let isValid = form.checkValidity();
    if (!isValid) {
      return false;
    }

    // If valid, send to the backend
    axios.post(`/api/events/`, this.state.event).then((res) => {
      if (this.props.onSaved) {
        this.props.onSaved(res.data);
      }
    }).catch((error) => {
      // BUG FIX: Handle promise rejection
      console.error('Failed to save event:', error);
      alert('Failed to save event. Please try again.');
    });
  }

  handleDelete(e) {
    var confirmed = window.confirm(
      "Are you sure you want to delete this item? This cannot be undone."
    );
    if (confirmed) {
      axios.delete(`/api/events/${this.state.event.id}`).then((res) => {
        if (this.props.onDeleted) {
          this.props.onDeleted(this.state.event.id);
        }
      }).catch((error) => {
        // BUG FIX: Handle promise rejection
        console.error('Failed to delete event:', error);
        alert('Failed to delete event. Please try again.');
      });
    }
  }

  handleCancel(e) {
    if (this.props.onCancel) {
      this.props.onCancel(e.target.value);
    }
  }

  render = () => {
    if (this.state.loading) {
      return <Loader />;
    }

    let event = this.state.event;
    return (
      <div className="form-container">
        <form
          key={event.id}
          id="event-form"
          onSubmit={this.handleSubmit}
        >
          <h1>
            {this.state.event.id
              ? 'Editing "' + event.name + '"'
              : "Creating Item"}
          </h1>
          <hr />
          <h2>Item Information</h2>

          <div className="form-group">
            <PrismInputText
              name="name"
              label="Name"
              value={event.name || ""}
              required
              onPrismChange={this.handlePrismChange("name")}
            />
          </div>

          <div className="form-group">
            <PrismInputTextarea
              name="description"
              label="Description"
              value={event.description || ""}
              onPrismChange={this.handlePrismChange("description")}
            />
          </div>

          <div className="form-group">
            <PrismInputText
              type="date"
              name="date"
              label="Date"
              value={event.date || ""}
              required
              onPrismChange={this.handlePrismChange("date")}
            />
          </div>

          <div className="form-group">
            <PrismSelect
              name="type"
              label="Type"
              value={event.type || ""}
              required
              options={[
                { value: "event", display: "Event" },
                { value: "task", display: "Task" },
              ]}
              onPrismChange={this.handlePrismChange("type")}
            />
          </div>

          <div className="form-group">
            <PrismSelect
              name="role"
              label="Permissions"
              value={event.role != null ? String(event.role) : ""}
              required
              hint="Minimum permissions required to view this item. This includes the selected role and each role above it."
              options={this.state.roles.map((role) => ({
                value: String(role.id),
                // `display`, not `label`: prism-select treats an option that
                // carries a `label` as an optgroup and maps over its children.
                display:
                  role.name && role.name.length > 0
                    ? role.name[0].toUpperCase() + role.name.slice(1)
                    : role.name || "",
              }))}
              onPrismChange={this.handlePrismChange("role")}
            />
          </div>
        </form>
        <hr />
        <h2>Links</h2>
        {this.state.event.EventLinks && this.state.event.EventLinks.length ? (
          <ul>
            {this.state.event.EventLinks.map((link, index) => {
              return (
                <li key={index}>
                  <a href={link.url}>{link.name}</a> (
                  <PrismButton
                    onPrismClick={() => this.handleLinkRemove(index)}
                    tone="danger"
                    display="link"
                    size="sm"
                  >
                    Remove Link
                  </PrismButton>
                  )
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="event-form-empty">
            No links yet! If you'd like to include links, use the form below.
          </p>
        )}
        <div className="event-link-card">
          <div className="event-link-card-header">Add Link</div>
          <div className="event-link-card-body">
            <form onSubmit={this.handleLinkAdd}>
              <div className="form-group">
                <PrismInputText
                  name="newLinkName"
                  label="Link Name"
                  value={this.state.newLinkName}
                  required
                  onPrismChange={(e) =>
                    this.setState({ newLinkName: e.detail?.value ?? "" })
                  }
                />
              </div>
              <div className="form-group">
                <PrismInputText
                  name="newLinkUrl"
                  label="Link Url"
                  value={this.state.newLinkUrl}
                  required
                  onPrismChange={(e) =>
                    this.setState({ newLinkUrl: e.detail?.value ?? "" })
                  }
                />
              </div>

              <PrismButton type="submit">Add Link</PrismButton>
            </form>
          </div>
        </div>
        <hr />
        {/* Outside the <form>; prism-button resolves `form` by id and calls requestSubmit(). */}
        <PrismButton type="submit" form="event-form">
          Save Item
        </PrismButton>{" "}
        {event.id ? (
          <PrismButton
            tone="danger"
            display="outline"
            onPrismClick={this.handleDelete}
          >
            Delete Item
          </PrismButton>
        ) : null}{" "}
        <PrismButton
          tone="secondary"
          display="outline"
          onPrismClick={this.handleCancel}
        >
          Cancel
        </PrismButton>
      </div>
    );
  };
}
